import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

import {isValidDate} from 'ApplicationEntry/models/Date';
import {InvalidZipCode, isValidZipCode, ValidZipCode, ZipCode} from 'ApplicationEntry/models/ZipCode';
import {FormNumber, FormNumberError, UserAssignedFormNumber} from './FormNumber';

import {Async} from 'Core/models/Async';

export class InvalidGroupBillingAgent {
  public readonly key = 'InvalidGroupBillingAgent';

  constructor(
    public readonly billingMode: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly issueDate: Date | '',
    public readonly formNumber: FormNumber,
    public readonly dateSigned: Date | '',
    public readonly electronicDelivery: boolean,
    public readonly ink: boolean,
    public readonly payor: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly payroll: boolean,
    public readonly directBill: boolean,
    public readonly achTransitNumber: StringUnion,
    public readonly achAccountNumber: StringUnion,
    public readonly achAccountType: StringUnion,
    public readonly achPayerName: StringUnion,
    public readonly achDraftDay: StringUnion,
    public readonly achAuthorizationDate: Date | '',
    public readonly pac: boolean,
    public readonly transitNumber: StringUnion,
    public readonly bankAccountNumber: StringUnion,
    public readonly dueDate: StringUnion,
    public readonly accountHolderName: StringUnion,
    public readonly bankType: StringUnion,
    public readonly authorizedDate: Date | '',
  ) {}
}

type BillingAgentProp = {
  billingMode: StringUnion;
  controlNumber: StringUnion;
  issueDate: Date | '';
  formNumber: FormNumber;
  dateSigned: Date | '';
  electronicDelivery: boolean;
  ink: boolean;
  payor: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  payroll: boolean;
  directBill: boolean;
  achTransitNumber: StringUnion;
  achAccountNumber: StringUnion;
  achAccountType: StringUnion,
  achPayerName: StringUnion,
  achDraftDay: StringUnion,
  achAuthorizationDate: Date | '',
  pac: boolean;
  transitNumber: StringUnion;
  bankAccountNumber: StringUnion;
  dueDate: StringUnion;
  accountHolderName: StringUnion;
  bankType: StringUnion;
  authorizedDate: Date | '';
};

export class ValidGroupBillingAgent {
  public static createFromApp({
    billingMode,
    controlNumber,
    issueDate,
    formNumber,
    dateSigned,
    electronicDelivery,
    ink,
    payor,
    state,
    zip,
    payroll,
    directBill,
    achTransitNumber,
    achAccountNumber,    
    achAccountType,
    achPayerName,
    achDraftDay,
    achAuthorizationDate,
    pac,
    transitNumber,
    bankAccountNumber,
    dueDate,
    accountHolderName,
    bankType,
    authorizedDate,
  }: BillingAgentProp) {
    return this.create(
      billingMode,
      controlNumber,
      issueDate,
      formNumber,
      dateSigned,
      electronicDelivery,
      ink,
      payor,
      state,
      zip,
      payroll,
      directBill,
      achTransitNumber,
      achAccountNumber,      
      achAccountType,
      achPayerName,
      achDraftDay,
      achAuthorizationDate,
      pac,
      transitNumber,
      bankAccountNumber,
      dueDate,
      accountHolderName,
      bankType,
      authorizedDate
    );
  }

  public static create(
    billingMode: StringUnion,
    controlNumber: StringUnion,
    issueDate: Date | '',
    formNumber: FormNumber,
    dateSigned: Date | '',
    electronicDelivery: boolean,
    ink: boolean,
    payor: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    payroll: boolean,
    directBill: boolean,
    achTransitNumber: StringUnion,
    achAccountNumber: StringUnion,
    achAccountType: StringUnion,
    achPayerName: StringUnion,
    achDraftDay: StringUnion,
    achAuthorizationDate: Date | '',
    pac: boolean,
    transitNumber: StringUnion,
    bankAccountNumber: StringUnion,
    dueDate: StringUnion,
    accountHolderName: StringUnion,
    bankType: StringUnion,
    authorizedDate: Date | '',
  ) {
    return isNonEmptyString(billingMode) && isValidZipCode(zip) && isValidDate(issueDate) && isValidDate(dateSigned)
      ? new ValidGroupBillingAgent(
          billingMode,
          controlNumber,
          issueDate,
          formNumber,
          dateSigned,
          electronicDelivery,
          ink,
          payor,
          state,
          zip,
          payroll,
          directBill,
          achTransitNumber,
          achAccountNumber,
          achAccountType,
          achPayerName,
          achDraftDay,
          achAuthorizationDate,
          pac,
          transitNumber,
          bankAccountNumber,
          dueDate,
          accountHolderName,
          bankType,
          authorizedDate,
        )
      : new InvalidGroupBillingAgent(
          billingMode,
          controlNumber,
          issueDate,
          formNumber,
          dateSigned,
          electronicDelivery,
          ink,
          payor,
          state,
          zip,
          payroll,
          directBill,
          achTransitNumber,
          achAccountNumber,
          achAccountType,
          achPayerName,
          achDraftDay,
          achAuthorizationDate,
          pac,
          transitNumber,
          bankAccountNumber,
          dueDate,
          accountHolderName,
          bankType,
          authorizedDate
        );
  }
  public readonly key = 'ValidGroupBillingAgent';

  constructor(
    public readonly billingMode: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly issueDate: Date | '',
    public readonly formNumber: FormNumber,
    public readonly dateSigned: Date | '',
    public readonly electronicDelivery: boolean,
    public readonly ink: boolean,
    public readonly payor: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly payroll: boolean,
    public readonly directBill: boolean,
    public readonly achTransitNumber: StringUnion,
    public readonly achAccountNumber: StringUnion,
    public readonly achAccountType: StringUnion,
    public readonly achPayerName: StringUnion,
    public readonly achDraftDay: StringUnion,
    public readonly achAuthorizationDate: Date | '',
    public readonly pac: boolean,
    public readonly transitNumber: StringUnion,
    public readonly bankAccountNumber: StringUnion,
    public readonly dueDate: StringUnion,
    public readonly accountHolderName: StringUnion,
    public readonly bankType: StringUnion,
    public readonly authorizedDate: Date | '',
  ) {}
}

export type GroupBillingAgent = ValidGroupBillingAgent | InvalidGroupBillingAgent;

export type GroupBillingAgentOptions<TReturn> = ToMatchOptions<GroupBillingAgent, 'key', TReturn>;

export const matchGroupBillingAgent = <TReturn>(
  billingAgent: GroupBillingAgent,
  options: GroupBillingAgentOptions<TReturn>
): TReturn => {
  switch (billingAgent.key) {
    case 'InvalidGroupBillingAgent':
      return options.InvalidGroupBillingAgent(billingAgent);
    case 'ValidGroupBillingAgent':
      return options.ValidGroupBillingAgent(billingAgent);
  }
};

export const EmptyGroupBillingAgent: GroupBillingAgent = new ValidGroupBillingAgent(
  new EmptyString(),
  new EmptyString(),
  '',
  new UserAssignedFormNumber('', '', '', '', ''),
  '',
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  new InvalidZipCode(''),
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  '',
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  ''
);

export const changeBillingMode = (billingMode: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, billingMode: NonEmptyString.create(billingMode)});

export const getAchTransitNumber = (achTransitNumber: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, achTransitNumber: NonEmptyString.create(achTransitNumber)});

export const getAchAccountNumber = (achAccountNumber: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, achAccountNumber: NonEmptyString.create(achAccountNumber)});

export const getAchAccounType = (achAccountType: string) => (app: GroupBillingAgent) =>
ValidGroupBillingAgent.createFromApp({...app, achAccountType: NonEmptyString.create(achAccountType)});  

export const getAchPayerName = (achPayerName: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, achPayerName: NonEmptyString.create(achPayerName)});

export const getAchDraftDay = (achDraftDay: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, achDraftDay: NonEmptyString.create(achDraftDay)});

export const changeControlNumber = (controlNumber: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({
    ...app,
    controlNumber: NonEmptyString.create(controlNumber),
  });

export const changeIssueDate = (issueDate: Date | '') => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, issueDate});

export const changeFormNumber = (formNumber: Async<UserAssignedFormNumber>) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({
    ...app,
    formNumber,
  });

export const changeUserFormNumber = (
  formNumber: string,
  documentShortName: string,
  fipscoDocumentName: string,
  documentLongName: string,
  documentName: string
) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({
    ...app,
    formNumber: UserAssignedFormNumber.create(
      formNumber,
      documentShortName,
      fipscoDocumentName,
      documentLongName,
      documentName
    ),
  });

export const changeDateSigned = (dateSigned: Date | '') => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, dateSigned});

export const changeAuthorizedDate = (achAuthorizationDate: Date | '') => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, achAuthorizationDate});

export const changeElectronicDelivery = (electronicDelivery: boolean) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, electronicDelivery});

export const changeInk = (ink: boolean) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, ink});

export const changePayor = (payor: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, payor: NonEmptyString.create(payor)});

export const changeState = (state: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, zip});

export const changeFormNumberError = (formNumber: string) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, formNumber: FormNumberError.create(formNumber)});

export const changePayroll = (payroll: boolean) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, payroll});

export const changeDirectBill = (directBill: boolean) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, directBill});

export const changePac = (pac: boolean) => (app: GroupBillingAgent) =>
  ValidGroupBillingAgent.createFromApp({...app, pac});

// export const changeTransit = (transit: string) => (app: GroupBillingAgent) =>
// ValidGroupBillingAgent.createFromApp({...app, transit: NonEmptyString.create(transit)});
  
// export const changeAccount = (account: string) => (app: GroupBillingAgent) =>
// ValidGroupBillingAgent.createFromApp({...app, account: NonEmptyString.create(account)});
